import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, MessageCircle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api, { newsletter } from '@/lib/api';
import { toast } from 'sonner';
import { buildPathForLocale, localizeInternalPath } from '@/lib/intl-seo';
import { isExactHomepagePath, requestHomepageRefresh } from '@/lib/homePageVisibility';

const FOOTER_LINK_I18N = {
  '/privacy': 'footer.privacyPolicy',
  '/privacy-policy': 'footer.privacyPolicy',
  '/terms': 'footer.termsOfService',
  '/terms-of-service': 'footer.termsOfService',
  '/cookies': 'footer.cookiePolicy',
  '/cookie-policy': 'footer.cookiePolicy',
  '/shipping': 'footer.shippingInfo',
  '/returns': 'footer.returnsRefunds',
  '/track': 'footer.trackOrder',
  '/contact': 'footer.contactUs',
  '/help': 'footer.helpCenter',
  '/support': 'footer.support',
  '/faq': 'footer.faq',
  '/products': 'nav.products',
  '/categories': 'nav.categories',
  '/seller-register': 'nav.becomeSeller',
  '/become-seller': 'nav.becomeSeller',
  '/seller': 'nav.sellerPanel',
  '/seller-guide': 'sellerGuide.title',
  '/careers': 'careers.title',
  '/supermarkets': 'nav.turkishSupermarkets',
  '/about': 'footer.aboutUs',
};

const FOOTER_COLUMN_TITLE_I18N = {
  'Hızlı Bağlantılar': 'footer.quickLinks',
  'Hızlı Linkler': 'footer.quickLinks',
  'Quick Links': 'footer.quickLinks',
  'Müşteri Hizmetleri': 'footer.customerService',
  'Customer Service': 'footer.customerService',
  'Kundenservice': 'footer.customerService',
  'Service client': 'footer.customerService',
  'Servicio al cliente': 'footer.customerService',
  'Hakkımızda': 'footer.aboutUs',
  'About Us': 'footer.aboutUs',
  'Satıcılar İçin': 'footer.forSellers',
  'For Sellers': 'footer.forSellers',
  'Destek': 'footer.support',
  'Support': 'footer.support',
  'Kategoriler': 'nav.categories',
  'Categories': 'nav.categories',
  'Yardım': 'footer.help',
  'Help': 'footer.help',
  'Türk Süpermarket': 'nav.turkishSupermarkets',
  'Türk Süpermarketleri': 'nav.turkishSupermarkets',
  'İletişim': 'footer.contactUs',
};

const FOOTER_LINK_TEXT_I18N = {
  'Satıcı Ol': 'nav.becomeSeller',
  'Satıcı Paneli': 'nav.sellerPanel',
  'Satıcı Rehberi': 'sellerGuide.title',
  'Kategoriler': 'nav.categories',
  'Hakkımızda': 'footer.aboutUs',
  'Kariyer': 'careers.title',
  'Kariyer — İş başvurusu': 'careers.title',
  'Yardım': 'footer.help',
  'Yardım Merkezi': 'footer.helpCenter',
  'Destek': 'footer.support',
  'destek': 'footer.support',
  'SSS': 'footer.faq',
  'İletişim': 'footer.contactUs',
  'Ürünler': 'nav.products',
  'Sipariş Takibi': 'footer.trackOrder',
  'İade & Değişim': 'footer.returnsRefunds',
  'Kargo Bilgisi': 'footer.shippingInfo',
  'Gizlilik Sözleşmesi': 'footer.privacyPolicy',
  'Gizlilik Politikası': 'footer.privacyPolicy',
  'Şartlar ve Koşullar': 'footer.termsOfService',
  'Kullanım Koşulları': 'footer.termsOfService',
  'Çerez Politikası': 'footer.cookiePolicy',
  'Geri Ödeme ve İade Politikası': 'footer.returnsRefunds',
};

/** Admin/footer’da tanımlı ama rotası olmayan veya alias gereken yollar */
const FOOTER_URL_CANONICAL = {
  '/faq': '/support',
  '/help': '/support',
  '/carriers': '/careers',
};

function normalizeFooterPath(url) {
  return (url || '').split('?')[0].replace(/\/$/, '') || '/';
}

function resolveFooterHref(url, langCode) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//') || url.startsWith('mailto:') || url.startsWith('tel:')) {
    return url;
  }
  const qIdx = url.indexOf('?');
  const pathOnly = normalizeFooterPath(qIdx >= 0 ? url.slice(0, qIdx) : url);
  const query = qIdx >= 0 ? url.slice(qIdx) : '';
  const canonical = FOOTER_URL_CANONICAL[pathOnly] || pathOnly;
  return localizeInternalPath(`${canonical}${query}`, langCode);
}

function resolveI18n(key, t) {
  if (!key) return null;
  const translated = t(key);
  return translated !== key ? translated : null;
}

function resolveFooterLinkLabel(url, fallback, t, langCode) {
  if (langCode === 'tr') return fallback;

  const path = normalizeFooterPath(url);
  const fromUrl = resolveI18n(FOOTER_LINK_I18N[path], t);
  if (fromUrl) return fromUrl;

  const fromText = resolveI18n(FOOTER_LINK_TEXT_I18N[(fallback || '').trim()], t);
  if (fromText) return fromText;

  return fallback;
}

function resolveFooterColumnTitle(title, t, langCode) {
  if (langCode === 'tr') return title;

  const key = FOOTER_COLUMN_TITLE_I18N[title];
  if (key) {
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return title;
}

// X (Twitter) Icon Component
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const currentLang = i18n.language || 'tr';
  const lc = (i18n.language || 'en').substring(0, 2);
  const homePath = buildPathForLocale(lc, []);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Re-render when language changes
  useEffect(() => {
    // Force re-render when language changes
  }, [currentLang]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/homepage/settings');
      setSettings(res.data);
    } catch (error) {
      console.error('Footer settings fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get social icon component
  const getSocialIcon = (iconName) => {
    switch (iconName) {
      case 'Twitter':
        return <XIcon className="w-4 h-4" />;
      case 'Facebook':
        return <Facebook className="w-4 h-4" />;
      case 'Instagram':
        return <Instagram className="w-4 h-4" />;
      case 'Youtube':
        return <Youtube className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Get translated content based on current language
  const getTranslatedFooter = () => {
    const langCode = (currentLang || 'tr').substring(0, 2);
    const isTurkish = langCode === 'tr';
    const translations = settings?.footer_translations || {};
    const langTrans = translations[langCode] || translations[currentLang];
    const rawColumns = settings?.footer_columns || [];

    const resolveColumns = (columns) =>
      columns.map((col, colIndex) => {
        const transCol = langTrans?.columns?.[colIndex];
        const title = transCol?.title || col.title;
        return {
          ...col,
          title: isTurkish ? title : resolveFooterColumnTitle(title, t, langCode),
          links: (col.links || []).map((link, linkIndex) => {
            const text = transCol?.links?.[linkIndex] || link.text;
            return {
              ...link,
              text: isTurkish ? text : resolveFooterLinkLabel(link.url, text, t, langCode),
            };
          }),
        };
      });

    if (isTurkish) {
      return {
        newsletterTitle: langTrans?.newsletter_title || settings?.footer_newsletter_title || t('footer.newsletter'),
        newsletterDesc: langTrans?.newsletter_desc || settings?.footer_newsletter_desc || t('footer.newsletterDesc'),
        tagline: langTrans?.tagline || settings?.footer_tagline || t('footer.tagline'),
        columns: resolveColumns(rawColumns),
      };
    }

    return {
      newsletterTitle: langTrans?.newsletter_title || t('footer.newsletter'),
      newsletterDesc: langTrans?.newsletter_desc || t('footer.newsletterDesc'),
      tagline: langTrans?.tagline || t('footer.tagline'),
      columns: resolveColumns(rawColumns),
    };
  };

  const translatedFooter = getTranslatedFooter();

  // Default values if settings not loaded
  const footerEnabled = settings?.footer_enabled !== false;
  const newsletterEnabled = settings?.footer_newsletter_enabled !== false;
  const newsletterTitle = translatedFooter.newsletterTitle;
  const newsletterDesc = translatedFooter.newsletterDesc;
  const brandName = settings?.footer_brand_name || 'Turkish E-Market';
  const tagline = translatedFooter.tagline;
  const socialLinks = settings?.footer_social_links || [];
  const appLinks = settings?.footer_app_links || {};
  const appLinksEnabled = appLinks.enabled !== false;
  const appleStoreUrl = (appLinks.apple_store || '').trim() || 'https://apps.apple.com';
  const googlePlayUrl = (appLinks.google_play || '').trim() || 'https://play.google.com/store/apps';
  const whatsappConfig = settings?.footer_whatsapp || { enabled: false };
  const footerColumns = translatedFooter.columns;
  const contactInfo = settings?.footer_contact || {};
  const bottomLinks = settings?.footer_bottom_links || [];
  const defaultCopyright = `© ${new Date().getFullYear()} ${brandName}. ${t('footer.allRightsReserved')}`;
  const footerLangTrans = settings?.footer_translations?.[lc] || settings?.footer_translations?.[currentLang];
  const copyright = lc === 'tr'
    ? (settings?.footer_copyright || defaultCopyright)
    : (footerLangTrans?.copyright || defaultCopyright);

  // Generate WhatsApp link
  const getWhatsAppLink = () => {
    if (!whatsappConfig.enabled || !whatsappConfig.phone) return null;
    const phone = whatsappConfig.phone.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(whatsappConfig.message || '');
    return `https://wa.me/${phone}${message ? `?text=${message}` : ''}`;
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('footer.emailRequired'));
      return;
    }
    setNewsletterSubmitting(true);
    try {
      const res = await newsletter.subscribe(email, currentLang.substring(0, 2));
      if (res.data?.already_subscribed) {
        toast.info(t('footer.subscribeAlready'));
      } else {
        toast.success(t('footer.subscribeSuccess'));
        if (res.data?.confirmation_email_queued) {
          toast.info(t('footer.subscribeEmailSent'), { duration: 5000 });
        }
      }
      setNewsletterEmail('');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('footer.subscribeError'));
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  if (!footerEnabled) return null;
  
  return (
    <footer className="bg-gray-100 text-gray-800 pb-[calc(4rem+env(safe-area-inset-bottom,0px))] sm:pb-0" data-testid="footer">
      {/* Newsletter */}
      {newsletterEnabled && (
        <div className="border-b border-gray-200">
          <div className="container mx-auto px-4 md:px-6 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Newsletter Form */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg font-serif text-gray-900 mb-1">{newsletterTitle}</h3>
                <p className="text-gray-600 text-sm mb-3">{newsletterDesc}</p>
                <form className="flex gap-2 max-w-sm mx-auto md:mx-0" onSubmit={handleNewsletterSubmit}>
                  <Input
                    type="email"
                    placeholder={t('auth.enterYourEmail')}
                    className="flex-1 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 h-9 text-sm"
                    data-testid="newsletter-email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={newsletterSubmitting}
                    required
                    autoComplete="email"
                  />
                  <Button
                    type="submit"
                    className="rounded-full px-4 h-9 text-sm"
                    data-testid="newsletter-submit"
                    disabled={newsletterSubmitting}
                  >
                    {newsletterSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('footer.subscribe')
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mağaza rozetleri + ETBİS — bülten kapalı olsa bile footer görünürken göster */}
      <div className="border-b border-gray-200 bg-gray-50/80">
        <div className="container mx-auto px-4 md:px-6 py-4">
          <div
            className={`flex flex-col md:flex-row flex-wrap items-center gap-6 ${
              appLinksEnabled ? 'justify-center md:justify-between' : 'justify-center'
            }`}
          >
            {appLinksEnabled && (
              <div className="text-center md:text-left">
                <p className="text-xs font-medium text-gray-700 mb-2">{t('home.downloadApp')}</p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                  <a
                    href={appleStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    data-testid="footer-app-store"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL || ''}/badges/app-store.svg`}
                      alt="Download on the App Store"
                      className="h-10 w-auto"
                      width={120}
                      height={40}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                  <a
                    href={googlePlayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    data-testid="footer-google-play"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL || ''}/badges/google-play.png`}
                      alt="Google Play'den edinin"
                      className="h-12 w-auto max-w-[155px]"
                      width={155}
                      height={60}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                </div>
              </div>
            )}
            <a
              href="https://www.eticaret.gov.tr/sitedogrulama/C26B0B46A54D47628DAF7C6B33E6D1B8"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 hover:opacity-90 transition-opacity"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_441a9660-2ab5-4230-89a3-891f41cbe524/artifacts/0l36tycp_image.png"
                alt="ETBİS - E-Ticaret Bilgi Sistemi"
                className="h-16 md:h-20 w-auto"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="container mx-auto px-4 md:px-6 py-6">
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-${Math.min(footerColumns.length + 2, 5)} gap-6`}>
          {/* Brand & Social */}
          <div>
            <Link
              to={homePath}
              className="flex items-center gap-2 mb-3"
              onClick={(event) => {
                if (!isExactHomepagePath(location.pathname, lc)) return;
                event.preventDefault();
                requestHomepageRefresh();
              }}
            >
              <img src="/logo.png" alt={brandName} className="h-10 w-auto" />
            </Link>
            <p className="text-gray-600 text-xs mb-3 leading-relaxed">
              {tagline}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-2 mb-3">
              {socialLinks.filter(s => s.enabled !== false).map((social, idx) => (
                <a 
                  key={social.id || `social-${idx}`}
                  href={social.url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center hover:bg-primary hover:text-white transition-colors text-gray-700"
                  data-testid={`social-${social.id || idx}`}
                >
                  {getSocialIcon(social.icon)}
                </a>
              ))}
            </div>
          </div>

          {/* Dynamic Columns */}
          {footerColumns.filter(col => col.enabled !== false).map((column, colIdx) => (
            <div key={column.id || `col-${colIdx}`}>
              <h4 className="text-gray-900 font-semibold text-sm mb-2">{column.title}</h4>
              <ul className="space-y-1 text-xs text-gray-700">
                {(column.links || []).filter(link => link.enabled !== false).map((link, linkIdx) => (
                  <li key={link.id || `link-${colIdx}-${linkIdx}`}>
                    {(link.url || '').startsWith('http') ? (
                      <a 
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        {link.text}
                      </a>
                    ) : (
                      <Link to={resolveFooterHref(link.url, lc)} className="hover:text-primary transition-colors">
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          {contactInfo.enabled !== false && (
            <div>
              <h4 className="text-gray-900 font-semibold text-sm mb-2">{contactInfo.title || t('footer.contactUs')}</h4>
              <ul className="space-y-1.5 text-xs text-gray-700">
                {contactInfo.address && (
                  <li className="flex items-start gap-1.5">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-primary" />
                    <span>{contactInfo.address}</span>
                  </li>
                )}
                {contactInfo.phone && (
                  <li className="flex items-center gap-1.5">
                    <Phone className="w-3 h-3 flex-shrink-0 text-primary" />
                    <a href={`tel:${contactInfo.phone}`} className="hover:text-primary transition-colors">
                      {contactInfo.phone}
                    </a>
                  </li>
                )}
                {contactInfo.email && (
                  <li className="flex items-center gap-1.5">
                    <Mail className="w-3 h-3 flex-shrink-0 text-primary" />
                    <a href={`mailto:${contactInfo.email}`} className="hover:text-primary transition-colors">
                      {contactInfo.email}
                    </a>
                  </li>
                )}
              </ul>

              {/* WhatsApp Support Button */}
              <div className="mt-4">
                {whatsappConfig.enabled && getWhatsAppLink() && (
                  <a
                    href={getWhatsAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white px-3 py-2 rounded-md hover:bg-[#128C7E] transition-colors shadow-sm hover:shadow-md"
                    data-testid="whatsapp-support-link"
                  >
                    <WhatsAppIcon className="w-5 h-5" />
                    <div className="text-left">
                      <div className="text-xs font-semibold leading-tight">{whatsappConfig.button_text || 'WhatsApp ile Destek'}</div>
                      <div className="text-[9px] leading-tight opacity-90">7/24 Canlı Destek</div>
                    </div>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-200">
        <div className="container mx-auto px-4 md:px-6 py-3">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-gray-600">
            <p>{copyright}</p>
            {bottomLinks.length > 0 && (
              <div className="flex gap-4">
                {bottomLinks.filter(link => link.enabled !== false).map((link, idx) => {
                  const translatedText = lc === 'tr'
                    ? link.text
                    : resolveFooterLinkLabel(link.url, link.text, t, lc);

                  return (
                    <Link 
                      key={link.id || `bottom-link-${idx}`}
                      to={resolveFooterHref(link.url, lc)} 
                      className="hover:text-primary transition-colors"
                    >
                      {translatedText}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
